import Editor, { EditorProps } from '@monaco-editor/react';
import { QueryTriggerContext } from '@/views/dataCloudStudio/components/Providers/components/QueryTriggerProvider';
import * as monaco from 'monaco-editor';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, OverflowList } from '@douyinfe/semi-ui';
import { EventManager, Icon } from '@/components';
import './CodeEditor.scss';
import px2rem from '@/utils/px2rem';
import { useTranslation } from 'react-i18next';
import { ParametersContext } from '@/views/dataCloudStudio/components/Providers/components/ParametersProvider';
import { ActiveTabContext } from '@/views/dataCloudStudio/components/Providers/components/ActiveTabProvider';
import { sqlFileDB } from '@/utils/db/dataCloud/sqlFileDB';
import { EditorRefContext } from '@/views/dataCloudStudio/components/Providers/components/EditorRefProvider';
import { TextSelectorContext } from '../../../SelectTextProvider';
import './index.scss';

export const EditorInsert = 'EditorInsert';

type CodeEditorProps = {
  editorKey?: string;
  onSave?: () => void;
  onRun?: () => void;
};

interface ParameterListProps {
  onOverflow: (isOverflowed: boolean) => void;
}

function ParameterList({ onOverflow }: ParameterListProps) {
  const scrollBarHeightKey = '--data-cloud-editor-parameter-list-scroll-bar-height';
  const { parameterList, edit } = useContext(ParametersContext);
  const activeTab = useContext(ActiveTabContext);
  const tabActiveKey = activeTab.current;
  const [focus, setFocus] = useState(-1);
  const overflowListRef = useRef<OverflowList>(null);

  useEffect(() => {
    const resizeOvserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { scrollWidth, clientWidth } = entry.target;
      if (scrollWidth === 0 || clientWidth === 0) {
        return;
      }
      const overflowed = scrollWidth > clientWidth;
      document.body.style.setProperty(scrollBarHeightKey, overflowed ? '6px' : '0px');
      onOverflow(overflowed);
    });

    const wheelListener = (e: WheelEvent) => {
      if (overflowListRef.current?.scroller) {
        if (e.deltaX === 0) {
          overflowListRef.current.scroller.scrollLeft += e.deltaY;
        }
      }
    };

    if (overflowListRef.current) {
      resizeOvserver.observe(overflowListRef.current.scroller);
      overflowListRef.current.scroller.addEventListener('wheel', wheelListener);
    }

    const scroller = overflowListRef.current?.scroller;
    return () => {
      resizeOvserver.disconnect();
      if (scroller) {
        scroller.removeEventListener('wheel', wheelListener);
      }
    };
  }, [onOverflow]);

  const renderOverflow = () => <div />;

  const renderParameterItem = (parameter: Record<string, any>, index: number): React.ReactElement => (
    <button
      key={parameter.name}
      className="ml-1.5 h-6 box-border m-0 p-0 px-2 py-[1px] flex border-solid border-[1px] border-[#444D56] rounded-[4px] hover:border-[#A1AAB0] shrink-0"
      style={{
        borderColor: index === focus ? '#A1AAB0' : '',
        backgroundColor: index === focus ? '#444D56' : ''
      }}
      type="button"
      onClick={() => edit(index)}
    >
      <span className="h-full flex items-center gap-1 text-xs">
        <span className="text-[#A1AAB0] text-[12px] ">{parameter.name}</span>
        <input
          className="parameter-value bg-transparent border-0 outline-0 text-white h-full w-[48px] text-[12px]"
          type={parameter.dataType.toLowerCase() === 'integer' ? 'number' : 'text'}
          onClick={(event) => {
            event.stopPropagation();
          }}
          value={parameter.defaultValue}
          onFocus={() => {
            setFocus(index);
          }}
          onBlur={() => {
            setFocus(-1);
          }}
          onChange={(event) => {
            if (!tabActiveKey) {
              return;
            }
            const newParameterList = parameterList.map((item) => ({
              ...item,
              defaultValue: item.name === parameter.name ? event.target.value : item.defaultValue
            }));
            sqlFileDB.sqlFile.update(tabActiveKey, {
              parameters: JSON.stringify(newParameterList)
            });
          }}
          placeholder="Default"
        />
      </span>
      <span className="h-full ml-2 flex items-center fill-[#A1AAB0] hover:fill-[#D1D7DD]">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="current" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.35595 1.49935C4.41359 1.16079 4.70795 0.889648 5.07705 0.889648H6.92103C7.2919 0.889648 7.57976 1.16109 7.6426 1.48921L7.64489 1.50116L7.78142 2.46598C7.91616 2.53467 8.04583 2.61063 8.17021 2.69287L9.06422 2.33203L9.06567 2.33145C9.40402 2.19612 9.78492 2.33403 9.96484 2.64352L9.96561 2.64486L10.8893 4.24513C11.0677 4.55908 10.9925 4.95587 10.7091 5.17964L10.7074 5.18097L9.94868 5.77465C9.95356 5.84774 9.95651 5.92289 9.95651 5.99961C9.95651 6.07646 9.95355 6.15175 9.94866 6.22496L10.7131 6.82455C10.9841 7.04567 11.07 7.43868 10.8849 7.75778C10.8848 7.75797 10.885 7.75758 10.8849 7.75778L9.96332 9.35437L9.96254 9.35571C9.77797 9.67321 9.39692 9.79387 9.07238 9.67128L9.06188 9.66731L8.16888 9.30675C8.04369 9.38968 7.91366 9.4654 7.77907 9.53367L7.64234 10.4999C7.5847 10.8385 7.29015 11.1096 6.92104 11.1096H5.07706C4.70799 11.1096 4.4136 10.8385 4.35596 10.4999L4.35361 10.486L4.21897 9.53324C4.08423 9.46455 3.95457 9.3886 3.83018 9.30636L2.93617 9.66719L2.9347 9.66778C2.59635 9.8031 2.21547 9.66519 2.03556 9.35571L2.03478 9.35437L1.1111 7.75409C0.932706 7.44015 1.00788 7.04335 1.29133 6.81958L1.29256 6.81861L2.04943 6.22496C2.04453 6.15175 2.04158 6.07646 2.04158 5.99961C2.04158 5.92276 2.04453 5.84748 2.04943 5.77427L1.285 5.17468C1.01405 4.95362 0.928153 4.56076 1.11307 4.24171C1.11291 4.242 1.11324 4.24143 1.11307 4.24171L2.03557 2.64348C2.22014 2.32605 2.60115 2.20534 2.92572 2.32795L2.93622 2.33192L3.82921 2.69248C3.95441 2.60955 4.08444 2.53383 4.21902 2.46555L4.35595 1.49935ZM5.31048 1.88965L5.13121 3.15654L4.86882 3.26622C4.62819 3.36681 4.40605 3.49663 4.20363 3.65386L3.97648 3.8303L2.78317 3.34866L2.09572 4.53966L3.10509 5.33137L3.06999 5.61217C3.05277 5.7499 3.04158 5.87682 3.04158 5.99961C3.04158 6.1224 3.05277 6.24931 3.06999 6.38706L3.10509 6.66786L2.09572 7.45956L2.78317 8.65057L3.9746 8.16969L4.20121 8.34349C4.40684 8.5012 4.63015 8.63322 4.86885 8.73301L5.13122 8.8427L5.31049 10.1096H6.68761L6.86689 8.84268L7.12927 8.733C7.3699 8.63242 7.59205 8.5026 7.79447 8.34536L8.02162 8.16893L9.21493 8.65057L9.90237 7.45956L8.89299 6.66786L8.9281 6.38706C8.94532 6.24932 8.95651 6.1224 8.95651 5.99961C8.95651 5.87682 8.94532 5.74992 8.9281 5.61216L8.89295 5.33099L9.90453 4.53943L9.21722 3.34866L8.02579 3.82954L7.79918 3.65574C7.59355 3.49802 7.37024 3.36601 7.13154 3.26621L6.86917 3.15653L6.6899 1.88965H5.31048ZM10.02 7.25578L10.0192 7.25712L10.02 7.25578ZM5.99903 4.88613C5.38315 4.88613 4.88555 5.38373 4.88555 5.99961C4.88555 6.6155 5.38315 7.1131 5.99903 7.1131C6.61492 7.1131 7.11252 6.6155 7.11252 5.99961C7.11252 5.38373 6.61492 4.88613 5.99903 4.88613ZM3.88555 5.99961C3.88555 4.83144 4.83086 3.88613 5.99903 3.88613C7.1672 3.88613 8.11252 4.83144 8.11252 5.99961C8.11252 7.16778 7.1672 8.1131 5.99903 8.1131C4.83086 8.1131 3.88555 7.16778 3.88555 5.99961Z"
            fill="current"
          />
        </svg>
      </span>
    </button>
  );

  return (
    <div className="max-w-[calc(100%-100px)]">
      <OverflowList
        ref={overflowListRef}
        items={parameterList.map((param) => ({ ...param, key: param.name }))}
        className="parameter-list"
        renderMode="scroll"
        visibleItemRenderer={renderParameterItem}
        overflowRenderer={renderOverflow}
      />
    </div>
  );
}

export default function CodeEditor(props: EditorProps & Omit<CodeEditorProps, ''>) {
  const { t } = useTranslation();
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();
  const globalEditorRef = useContext(EditorRefContext);
  const { add } = useContext(ParametersContext);
  const sqlQuery = useContext(QueryTriggerContext);
  const activeTab = useContext(ActiveTabContext);
  const parameters = useContext(ParametersContext);
  const textSelector = useContext(TextSelectorContext);
  const [lazyQueryNonce, setLazyQueryNonce] = useState(0);
  const prevQueryNonce = useRef(0);
  const [overflow, setOverflow] = useState(false);

  const {
    editorKey,
    options,
    onSave,
    language,
    value,
    onChange
  } = props;

  const insertText = (text: string): void => {
    const selection = editorRef?.current?.getSelection();
    const id = {
      major: 1,
      minor: 1
    };
    const op = {
      identifier: id,
      range: selection,
      text,
      forceMoveMarkers: true
    };
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    editorRef?.current?.executeEdits('my-source', [op]);
  };

  useEffect(() => {
    const listener = EventManager.subscribe(
      `${EditorInsert}:${editorKey}`,
      ({
        detail: { method, text }
      }: {
        detail: {
          method: 'insert' | 'focus';
          text: string;
        };
      }): void => {
        if (method === 'insert') {
          insertText(text);
        } else if (method === 'focus') {
          editorRef?.current?.focus();
        }
      }
    );

    return () => {
      listener();
    };
  }, [editorKey]);

  const executeQuery = useCallback(() => {
    if (!activeTab.current) {
      return;
    }

    const selection = editorRef?.current?.getSelection();
    let selectionText;
    if (selection) {
      selectionText = editorRef?.current?.getModel()?.getValueInRange(selection);
    }
    if (selectionText && selectionText.length > 0) {
      sqlQuery.request(activeTab.current, selectionText, parameters.parameterList);
    } else {
      const editorValue = editorRef.current?.getValue();
      if (!editorValue) {
        return;
      }
      sqlQuery.request(activeTab.current, editorValue, parameters.parameterList);
    }
  }, [activeTab, parameters.parameterList, sqlQuery]);

  useEffect(() => {
    if (lazyQueryNonce === 0) return;
    if (prevQueryNonce.current === lazyQueryNonce) return;
    prevQueryNonce.current += 1;
    executeQuery();
  }, [executeQuery, lazyQueryNonce]);

  /**
   * format code
   */
  const handleFormatCode = (): void => {
    editorRef?.current?.getAction('editor.action.formatDocument')?.run();
  };

  const onEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor): void => {
    // here is the editor instance
    // you can store it in `useRef` for further usage

    if (editorKey) {
      globalEditorRef.register(editorKey, editor);
    }

    editor.updateOptions({
      autoClosingBrackets: 'never'
    });

    editorRef.current = editor;

    editorRef.current?.setPosition({
      lineNumber: 1000,
      column: 1000
    });

    editorRef.current?.focus();

    /* eslint-disable no-bitwise */
    editorRef.current?.addAction({
      id: 'run',
      label: 'Run Query',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter],
      run(): void | Promise<void> {
        setLazyQueryNonce((prev) => prev + 1);
      }
    });

    editorRef.current?.addAction({
      id: 'save',
      label: 'Save Query',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
      run(): void | Promise<void> {
        if (onSave) {
          onSave();
        }
      }
    });

    editorRef.current?.onDidChangeCursorSelection(() => {
      const selection = editorRef?.current?.getSelection();
      let selectionText;
      if (!selection) {
        selectionText = '';
      } else {
        selectionText = editorRef?.current?.getModel()?.getValueInRange(selection) ?? '';
      }
      textSelector.select(selectionText);
    });
  };

  const onAddParameterClick = () => {
    add();
  };

  const renderParametersBottomBar = () => (
    <div className={`box-border ${overflow ? 'h-[45px]' : 'h-[40px]'} w-full px-2.5 pt-2 pb-0 flex gap-2 parameter-wrapper`}>
      <ParameterList onOverflow={(isOverflowed) => {
        setOverflow(isOverflowed);
      }}
      />
      <button
        className="box-border h-6 border-[#444D56] border-[1px] border-solid rounded-[4px] px-[9px] text-[#A1AAB0] flex gap-2 items-center text-xs hover:border-[#A1AAB0] active:bg-[#444D56]"
        type="button"
        onClick={onAddParameterClick}
      >
        <span>+</span>
        <span>{t('dataCloud.parameter')}</span>
      </button>
    </div>
  );

  return (
    <div className="h-full">
      <div
        className="code-editor"
        style={{
          height: overflow ? 'calc(100% - 48px)' : 'calc(100% - 40px)'
        }}
      >
        <Editor
          theme="vs-dark"
          onMount={onEditorDidMount}
          options={{
            minimap: {
              enabled: false
            },
            formatOnPaste: false,
            formatOnType: false,
            fontSize: px2rem.calcPXByScreen(14),
            wordWrap: 'on',
            cursorWidth: 0,
            contextmenu: false,
            ...options
          }}
          language={language}
          defaultValue={value}
          onChange={onChange}
        />
        <Button
          icon={<Icon icon="common/format" className="icon" />}
          className="btn-format"
          id="code-editor__format"
          theme="solid"
          onClick={handleFormatCode}
        />
      </div>
      {renderParametersBottomBar()}
    </div>
  );
}
